<script setup lang="ts">
/**
 * Avatar of a person, a group or a channel: initials on a coloured disc or on
 * a rounded square.
 *
 * Without colour props the fill is the brand token, so every avatar wears the
 * scheme's one brand colour (I-2154). `tone` (the colour means something) or
 * `hue` (a category, e.g. a group channel in violet) pick other token colours —
 * solid by default, tinted with `emphasis="soft"`. A numeric `hue` is the one
 * way to a computed gradient (calendar lanes). `colors` paints colours from data
 * instead (the calendars of a person): one fills the disc, several share it in
 * equal slices, none leaves an empty ring.
 *
 * `initials` sets the text explicitly („28“ for an order channel, „FR“ for
 * Freigaben); the default slot replaces it with an icon (a cog for System).
 * `shape="rounded"` is the rounded square for channels and groups.
 *
 * `src` shows a photo instead (a URL the caller builds — Gravatar, upload);
 * while it loads, and if it fails, the initials stay. `status` adds a presence
 * dot at the bottom-right edge, ringed in the surface colour (override the
 * ring with `--avatar-ring` on a coloured background, e.g. a navbar).
 *
 * The avatar is an image named `name` (plus the status, if any). Next to a
 * visible name pass `decorative`, so screen readers do not hear the name twice
 * — then say the status in the visible text too.
 */
import { computed, ref, watch } from 'vue';
import type { NxHue } from '../lib/hue';
import { accentVars, type NxAccentTone } from '../Panel/accent';

export type NxAvatarShape = 'circle' | 'rounded';
/** `solid` = full fill with on-colour text, `soft` = tinted fill with coloured text. */
export type NxAvatarEmphasis = 'solid' | 'soft';
/** Presence shown as a dot. */
export type NxAvatarStatus = 'online' | 'away' | 'busy' | 'offline';

const STATUS_LABELS: Record<NxAvatarStatus, string> = {
  online: 'Online',
  away: 'Abwesend',
  busy: 'Beschäftigt',
  offline: 'Offline',
};

const props = withDefaults(
  defineProps<{
    name: string;
    /** Logical px at 100 %. */
    size?: number;
    /** Text instead of the initials derived from `name` („28“, „FR“). */
    initials?: string;
    /** Intent colour; without one (and without `hue`) the avatar is `brand`. */
    tone?: NxAccentTone;
    /**
     * A category hue (token colours, wins over `tone`) — or a number 0–360
     * for a gradient on that hue.
     */
    hue?: NxHue | number;
    /** How `tone` / `hue` fill the avatar. A numeric-hue gradient is always solid. */
    emphasis?: NxAvatarEmphasis;
    /**
     * Any CSS colours from data; wins over `tone` and `hue`. One fills the
     * avatar, several share it in equal slices, an empty list is an empty ring.
     */
    colors?: readonly string[];
    shape?: NxAvatarShape;
    /** Hidden from assistive technology: the name is already written next to it. */
    decorative?: boolean;
    /** Photo URL; the initials show while it loads and when it fails. */
    src?: string;
    /** Presence dot. */
    status?: NxAvatarStatus;
    /** Names of the presence states (tooltip and accessible name). */
    statusLabels?: Partial<Record<NxAvatarStatus, string>>;
  }>(),
  {
    size: 24,
    initials: undefined,
    tone: undefined,
    hue: undefined,
    emphasis: 'solid',
    colors: undefined,
    shape: 'circle',
    decorative: false,
    src: undefined,
    status: undefined,
    statusLabels: undefined,
  }
);

/** The photo failed to load: fall back to the initials until `src` changes. */
const imageFailed = ref(false);
const imageLoaded = ref(false);
watch(
  () => props.src,
  () => {
    imageFailed.value = false;
    imageLoaded.value = false;
  }
);
const showImage = computed(() => !!props.src && !imageFailed.value);

const statusLabel = computed(() =>
  props.status ? (props.statusLabels?.[props.status] ?? STATUS_LABELS[props.status]) : ''
);
const accessibleName = computed(() =>
  statusLabel.value ? `${props.name} (${statusLabel.value})` : props.name
);

const slots = defineSlots<{
  /** Icon instead of the initials, e.g. a cog for System. */
  default?(): unknown;
}>();

const text = computed(() => {
  const explicit = props.initials?.trim();
  if (explicit) return explicit;
  const parts = props.name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
});

/** A named hue or a tone switches to token colours; a numeric hue stays a gradient. */
const tokenHue = computed(() => (typeof props.hue === 'string' ? props.hue : undefined));
const numericHue = computed(() => (typeof props.hue === 'number' ? props.hue : undefined));
// No colour asked for → the brand token: avatars, entity chips and buttons share
// the scheme's one brand colour instead of a hard-coded blue of their own (I-2154).
const tone = computed<NxAccentTone | undefined>(
  () => props.tone ?? (numericHue.value === undefined ? 'brand' : undefined)
);
const colored = computed(() => !props.colors && !!(tokenHue.value || tone.value));

const dataFill = computed(() => {
  const colors = props.colors;
  if (!colors?.length) return undefined;
  if (colors.length === 1) return colors[0];
  const step = 360 / colors.length;
  const slices = colors.map((color, i) => `${color} ${i * step}deg ${(i + 1) * step}deg`);
  return `conic-gradient(from -45deg, ${slices.join(', ')})`;
});

const gradient = computed(() => {
  const h1 = ((Math.round(numericHue.value ?? 0) % 360) + 360) % 360;
  const h2 = (h1 + 22) % 360;
  return `linear-gradient(135deg, hsl(${h1} 52% 42%), hsl(${h2} 58% 54%))`;
});

// Three characters and more („ABC“) get a smaller font so they still fit.
const fontFactor = computed(() => (!slots.default && text.value.length > 2 ? 0.32 : 0.4));

// `size` is logical px at 100 %: rendered as size-unit multiples so the avatar
// follows the root font scale and the surrounding scale scope.
const style = computed(() => ({
  '--avatar-size': `calc(${props.size / 16} * var(--nxt-size-unit))`,
  width: `calc(${props.size / 16} * var(--nxt-size-unit))`,
  height: `calc(${props.size / 16} * var(--nxt-size-unit))`,
  fontSize: `calc(${Math.round(props.size * fontFactor.value) / 16} * var(--nxt-size-unit))`,
  ...(props.colors
    ? dataFill.value && { background: dataFill.value }
    : colored.value
      ? accentVars(tone.value, tokenHue.value, '--avatar-accent')
      : { background: gradient.value }),
}));
</script>

<template>
  <div
    class="nx-avatar"
    :class="[
      `shape-${shape}`,
      colored ? ['is-colored', `emphasis-${emphasis}`] : null,
      { 'has-image': showImage && imageLoaded, 'is-empty': colors?.length === 0 },
    ]"
    :style="style"
    :title="decorative ? undefined : accessibleName"
    :role="decorative ? undefined : 'img'"
    :aria-label="decorative ? undefined : accessibleName"
    :aria-hidden="decorative ? 'true' : undefined"
  >
    <span v-if="$slots.default" class="nx-avatar__icon"><slot /></span>
    <template v-else>{{ text }}</template>
    <img
      v-if="showImage"
      class="nx-avatar__image"
      :class="{ 'is-loaded': imageLoaded }"
      :src="src"
      alt=""
      loading="lazy"
      decoding="async"
      referrerpolicy="no-referrer"
      data-testid="nx-avatar-image"
      @load="imageLoaded = true"
      @error="imageFailed = true"
    />
    <span
      v-if="status"
      class="nx-avatar__status"
      :class="`status-${status}`"
      data-testid="nx-avatar-status"
    />
  </div>
</template>

<style scoped>
/* The fill comes from token colours through the local --avatar-accent-*
   properties — `brand` unless the caller picks a tone or hue (I-2154). Only a
   numeric hue paints the computed gradient, with white text. */
.nx-avatar {
  position: relative;
  border-radius: var(--nxt-radius-full);
  display: grid;
  place-items: center;
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-on-accent);
  flex-shrink: 0;
  line-height: 1;
  white-space: nowrap;
}
/* The radius tokens are em: they follow the avatar's font size, which follows
   `size`, so the corner stays proportional at every size. */
.nx-avatar.shape-rounded {
  border-radius: var(--nxt-radius-lg);
}
.nx-avatar.is-colored.emphasis-solid {
  background: var(--avatar-accent);
  color: var(--avatar-accent-on);
}
.nx-avatar.is-colored.emphasis-soft {
  background: var(--avatar-accent-subtle-bg);
  color: var(--avatar-accent-fg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--avatar-accent-border);
}
.nx-avatar.is-empty {
  color: var(--nxt-fg-subtle);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 1.5) var(--nxt-border);
}
/* The photo lies over the initials and fades in once loaded, so a slow or
   broken image never leaves an empty disc. */
.nx-avatar__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  opacity: 0;
  transition: opacity var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-avatar__image.is-loaded {
  opacity: 1;
}
/* Presence dot on the bottom-right edge, cut out of the avatar by a ring in
   the surface colour (`--avatar-ring` for coloured backgrounds). */
.nx-avatar__status {
  position: absolute;
  right: calc(var(--avatar-size) * -0.04);
  bottom: calc(var(--avatar-size) * -0.04);
  width: max(var(--nxt-dot-size-sm), calc(var(--avatar-size) * 0.3));
  height: max(var(--nxt-dot-size-sm), calc(var(--avatar-size) * 0.3));
  border-radius: var(--nxt-radius-full);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--avatar-ring, var(--nxt-surface-bg));
}
.nx-avatar__status.status-online {
  background: var(--nxt-success-bg);
}
.nx-avatar__status.status-away {
  background: var(--nxt-warning-bg);
}
.nx-avatar__status.status-busy {
  background: var(--nxt-danger-bg);
}
.nx-avatar__status.status-offline {
  background: var(--avatar-ring, var(--nxt-surface-bg));
  box-shadow:
    inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-fg-muted),
    0 0 0 calc(var(--nxt-hairline) * 2) var(--avatar-ring, var(--nxt-surface-bg));
}
@media (prefers-reduced-motion: reduce) {
  .nx-avatar__image {
    transition: none;
  }
}
/* An icon is drawn a little larger than the initials (about half the avatar). */
.nx-avatar__icon {
  display: inline-flex;
  font-size: 1.375em;
}
</style>
